import React from "react";
import { ToastProvider, ToastViewport } from "@/components/ui/toast";
import { useToastContext } from "@/components/ui/toasts/hooks";
import type { ToasterToast } from "@/components/ui/toasts/types";
import { ToasterRow } from "@/components/ui/toasterRow";

const TOAST_REMOVE_DELAY = 12000;
const TOAST_LIMIT = 1;

export function Toaster() {
  const { state: toasts, setState } = useToastContext();
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const toastsToShow = React.useMemo(() => toasts.slice(0, TOAST_LIMIT), [toasts]);

  const closeToast = React.useCallback(
    (id: string) => {
      setState(state => state.map(toast => (toast.id === id ? { ...toast, open: false } : toast)));
    },
    [setState],
  );

  React.useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    [],
  );

  const removeToastAfterDelay = React.useCallback(
    (toast: ToasterToast) => {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        closeToast(toast.id);
      }, toast.duration || TOAST_REMOVE_DELAY);
    },
    [closeToast],
  );

  React.useEffect(() => {
    toastsToShow.forEach(toast => {
      removeToastAfterDelay(toast);
    });
  }, [toastsToShow, removeToastAfterDelay]);

  return (
    <ToastProvider>
      {toasts.map(toast => (
        <ToasterRow key={toast.id} toast={toast} closeToast={closeToast} />
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
